import type { Element } from '@xmpp/xml'
import { nanoid } from 'nanoid'
import invariant from 'tiny-invariant'
import { unescapeContent } from '@chilipiper/utils'
import { captureMessage } from '@chilipiper/sentry'
import { Affiliations } from '../../chat-data/types'
import {
  getArchiveStanza,
  getDelayStanza,
  getDelayStanzaStamp,
  getForwardedStanza,
  getGroupchatStanzaFrom,
  getMessageBodyStanza,
  getMessageBodyStanzaText,
  getMessageStanza,
  getStanzaId,
  getMessageStanzaData,
  isGroupchatStanza,
  isMessageStanza,
  getStanzaType,
  isUserParticipatingInRoom,
  isAffiliationsStanza,
} from '../helpers'
import { PayloadIn, TypedEnvelope } from '../types'
import { getRoomAndUserId } from './getRoomAndUserId'

const safeJsonParse = (messageBody: string) => {
  try {
    return JSON.parse(messageBody)
  } catch (e) {
    return null
  }
}

export const parseMessageBody = (messageBody?: string): TypedEnvelope | null => {
  if (!messageBody) {
    return null
  }
  const unescapedMessageBodyReplaceOnce = unescapeContent(messageBody.replace(/\\"/g, '"'))
  const result = safeJsonParse(unescapedMessageBodyReplaceOnce ?? '') as TypedEnvelope
  if (result) {
    return result
  }

  const unescapedMessageBodyReplaceTwice = unescapeContent(
    messageBody.replace(/\\"/g, '"').replace(/\\"/g, '"')
  )
  const unescapedResultReplaceTwice = safeJsonParse(
    unescapedMessageBodyReplaceTwice ?? ''
  ) as TypedEnvelope
  if (unescapedResultReplaceTwice) {
    // TODO: assess if this is still needed and remove if not needed anymore https://floatingapps.atlassian.net/browse/CHAT-2738
    captureMessage('parseMessageBody fallback: "unescape + replace twice"')
    return result
  }

  // Without unescaping because we didn't escape the before 29.07.2025
  const messageBodyReplaceOnce = messageBody.replace(/\\"/g, '"')
  const resultWithoutUnescape = safeJsonParse(messageBodyReplaceOnce ?? '') as TypedEnvelope
  if (resultWithoutUnescape) {
    // TODO: assess if this is still needed and remove if not needed anymore https://floatingapps.atlassian.net/browse/CHAT-2738
    captureMessage('parseMessageBody fallback: "replace"')
    return resultWithoutUnescape
  }

  // Without unescaping because we didn't escape the before 29.07.2025
  const messageBodyReplaceTwice = messageBody.replace(/\\"/g, '"').replace(/\\"/g, '"')
  const resultReplaceTwice = safeJsonParse(messageBodyReplaceTwice ?? '') as TypedEnvelope
  if (resultReplaceTwice) {
    // TODO: assess if this is still needed and remove if not needed anymore https://floatingapps.atlassian.net/browse/CHAT-2738
    captureMessage('parseMessageBody fallback: "replace twice"')
    return resultReplaceTwice
  }

  return null
}

export const parseGroupchatStanza = (stanza: Element): PayloadIn | undefined => {
  if (!isGroupchatStanza(stanza)) {
    return
  }
  const messageFrom = getGroupchatStanzaFrom(stanza)
  const lastMessageBodyStanza = getMessageBodyStanza(stanza)
  const messageBody = getMessageBodyStanzaText(lastMessageBodyStanza)
  const { userId, roomId } = getRoomAndUserId(messageFrom)
  const timestamp = getDelayStanzaStamp(getDelayStanza(stanza))
  if (!userId || !roomId) {
    return
  }
  try {
    const envelope = parseMessageBody(messageBody)
    const bodyTimestamp = !!envelope && 'createdAt' in envelope ? envelope.createdAt : undefined
    const bodyId =
      !!envelope && 'payload' in envelope && 'id' in envelope.payload
        ? envelope.payload.id
        : undefined
    const id = getStanzaId(stanza) ?? bodyId ?? nanoid()
    const baseEvent = {
      roomId,
      sent: true,
      author: userId,
      statuses: {},
      timestamp: timestamp ?? bodyTimestamp,
      id,
    } as const
    // TODO: handle all these types - https://floatingapps.atlassian.net/browse/CHAT-690
    if (
      !envelope ||
      !('payload' in envelope) ||
      envelope.payload.type === 'ConversationSessionExpired' ||
      envelope.payload.type === 'InternalServerError' ||
      envelope.payload.type === 'DecodingFailure' ||
      envelope.payload.type === 'SystemConversationAssigned' ||
      envelope.payload.type === 'SystemConversationReassigned' ||
      envelope.payload.type === 'SystemRepsHaveFailedToRespond' ||
      envelope.payload.type === 'MeetingSlotSelectionCancelled' ||
      envelope.payload.type === 'NodeExecutionResumed' ||
      envelope.payload.type === 'NodeExecutionTimedout' ||
      envelope.payload.type === 'RepSentMessage' ||
      envelope.payload.type === 'Start' ||
      envelope.payload.type === 'MeetingSchedulingFailed' ||
      envelope.payload.type === 'MeetingSchedulingRequested' ||
      envelope.payload.type === 'WaitingForRepHasTimedout'
    ) {
      return undefined
    }
    return {
      ...baseEvent,
      ...envelope.payload,
      envelopeId: 'envelopeId' in envelope ? envelope.envelopeId : undefined,
    }
  } catch (err) {
    console.error(err)
    // TODO https://floatingapps.atlassian.net/browse/CPP-9717
    return
  }
}

export const parseArchivedGroupchatStanza = (stanza: Element): PayloadIn | undefined => {
  const archiveStanza = getArchiveStanza(stanza)
  const archiveForwarded = getForwardedStanza(archiveStanza)
  const messageStanza = getMessageStanza(archiveForwarded)
  if (!messageStanza || !isGroupchatStanza(messageStanza)) {
    return
  }
  const timestamp = getDelayStanzaStamp(getDelayStanza(archiveForwarded))

  const event = parseGroupchatStanza(messageStanza)
  if (!event) {
    return
  }
  return {
    ...event,
    paginationId: archiveStanza?.getAttr('id'),
    timestamp,
  }
}

export const parseFailedGroupchatStanza = (stanza: Element) => {
  if (
    !isMessageStanza(stanza) ||
    getStanzaType(stanza) !== 'error' ||
    !isUserParticipatingInRoom(stanza)
  ) {
    return
  }

  const { id, from } = getMessageStanzaData(stanza)
  const { roomId } = getRoomAndUserId(from)

  if (!roomId) {
    return
  }

  return {
    id,
    roomId,
    author: 'bot',
    timestamp: new Date().toISOString(),
    type: 'Error',
    statuses: {
      [`${roomId ?? ''}_guest`]: 'failed',
    },
    sent: true,
  } as Extract<PayloadIn, { type: 'Error' }>
}

export const parseAffiliationsStanza = (stanza: Element): Affiliations | undefined => {
  if (!isAffiliationsStanza(stanza)) {
    return
  }

  const { from } = getMessageStanzaData(stanza)
  const { roomId } = getRoomAndUserId(from)
  invariant(roomId, 'Room ID not found')
  const user = stanza?.getChild('x')?.getChild('user')
  const affiliation: 'none' | 'member' | 'owner' = user?.getAttr('affiliation') ?? ''
  if (affiliation !== 'member' && affiliation !== 'none' && affiliation !== 'owner') {
    throw new Error(`Unknown affiliation "${affiliation}"`)
  }

  const version = stanza.getChild('x')?.getChild('version')?.getText()
  const previousVersion = stanza.getChild('x')?.getChild('prev-version')?.getText()
  const userId = getMessageBodyStanzaText(user)?.split('@')[0]
  invariant(userId, 'User ID not found')

  return {
    type: 'Affiliations',
    userId,
    roomId,
    affiliation,
    version,
    previousVersion,
  }
}

export const parseArchivedAffiliationsStanza = (stanza: Element): Affiliations | undefined => {
  const archiveStanza = getArchiveStanza(stanza)
  const archiveForwarded = getForwardedStanza(archiveStanza)
  const messageStanza = getMessageStanza(archiveForwarded)
  if (!messageStanza || !isAffiliationsStanza(messageStanza)) {
    return
  }

  const timestamp = getDelayStanzaStamp(getDelayStanza(archiveForwarded))
  const affiliations = parseAffiliationsStanza(messageStanza)
  if (!affiliations) {
    return
  }

  return {
    ...affiliations,
    timestamp,
    paginationId: archiveStanza?.getAttr('id'),
  }
}
